import { axiosInstance } from "api/axios.instance"
import { TProcessingGeneralFeedbackQuery, TUpdateDrawingPayload } from "types/drawer.type"
import { TAllFilesTable } from "types/table.type"
import type { TDrawingOwnerChangeJobResponse, TDrawingOwnerChangeStartResponse } from "types/drawingOwnerChange.type"

class DrawingsService {
	static BASE_URL = "/drawings"

	async getDrawings(tableParams: TAllFilesTable["tableParams"]) {
		const params = new URLSearchParams()
		params.append("page", tableParams.pagination.current.toString())

		const owner = tableParams.filter.owner
		if (owner && owner !== "all") {
			params.append("owner", owner)
		}

		const fileName = tableParams.filter.fileName
		if (fileName) {
			params.append("filterByName", fileName)
		}

		const statuses = tableParams.filter.status || []
		if (statuses.length > 0) {
			for (const status of statuses) {
				params.append("status", String(status))
			}
		}

		const user = tableParams.filter.user
		if (user) {
			params.append("user", user)
		}

		return axiosInstance.get(`${DrawingsService.BASE_URL}/`, {
			params,
			validateStatus: (status) => {
				return (status >= 200 && status < 300) || status === 404
			},
		})
	}

	async getDrawing(id: number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${id}/`)
	}

	async updateDrawing(id: number, payload: TUpdateDrawingPayload) {
		return axiosInstance.patch(`${DrawingsService.BASE_URL}/${id}/`, payload)
	}

	async getDrawingWithProcessings(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/`)
	}

	async getDocuments(drowingId: number, processingId: string | number | null, drawingStatus: string) {
		const urlWithoutProcessing = `${DrawingsService.BASE_URL}/${drowingId}/documents`
		const urlWithProcessings = `${DrawingsService.BASE_URL}/${drowingId}/processings/${processingId}/documents`
		const url = drawingStatus === "uploaded" ? urlWithoutProcessing : urlWithProcessings

		return axiosInstance.get(url)
	}

	async getDrawingKeywords(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/keywords/drawing/`)
	}

	async getSpecificationKeywords(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(
			`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/keywords/specification/`,
		)
	}

	async getDrawingKeywordsConfig(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(
			`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/key-phrases-config/drawing/`,
		)
	}

	async getDrawingSpecificationKeywordsConfig(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(
			`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/key-phrases-config/specification/`,
		)
	}

	async getDrawingObjects(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/objects-found`)
	}

	async getDrawingObjectsConfig(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/objects-config/`)
	}

	async delDrawing(id: number | undefined) {
		return axiosInstance.delete(`${DrawingsService.BASE_URL}/${id}/`)
	}

	async getProcessingRuns(id: number) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${id}/processings/`)
	}

	async duplicateDrawing(id: number) {
		return axiosInstance.post(`${DrawingsService.BASE_URL}/${id}/duplicate/`)
	}

	// Starts the background job that moves the drawing to another user (REQ-473); poll it with
	// `getOwnerChangeStatus`. Rejects with 409 while the drawing is busy.
	async startOwnerChange(drawingId: number, newOwnerId: number) {
		return axiosInstance.post<TDrawingOwnerChangeStartResponse>(`${DrawingsService.BASE_URL}/${drawingId}/change-owner/`, {
			new_owner_id: newOwnerId,
		})
	}

	async getOwnerChangeStatus(drawingId: number, jobId: number) {
		return axiosInstance.get<TDrawingOwnerChangeJobResponse>(
			`${DrawingsService.BASE_URL}/${drawingId}/change-owner/${jobId}/`,
		)
	}

	async postProcessingGeneralFeedback({ formData, drawingId, processingId }: TProcessingGeneralFeedbackQuery) {
		return axiosInstance.post(
			`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/generalFeedback/`,
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
				timeout: 0,
			},
		)
	}

	async postGeneralFeedback(formData: FormData, drawingId: number) {
		return axiosInstance.post(`${DrawingsService.BASE_URL}/${drawingId}/generalFeedback/`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
			timeout: 0,
		})
	}

	async getDrawingVersions(drawingId: number | null, processingId: string | number | null) {
		return axiosInstance.get(`${DrawingsService.BASE_URL}/${drawingId}/processings/${processingId}/sw-versions`)
	}
}

export default new DrawingsService()
